/**
 * Translation utilities for mapping legacy DefaultGroup/DefaultView values
 * to new UI grouping/view identifiers.
 *
 * Legacy values come from the server's Menu configuration and use different
 * identifiers than the new UI's grouping options.
 */

/**
 * Page context types for determining which translation map to use.
 * Different pages have different valid grouping options.
 */
export type PageContext =
  | 'accounts'
  | 'holdings'
  | 'cashflow'
  | 'taxlots'
  | 'assetAllocation'
  | 'diversifications'
  | 'balanceSheet';

/**
 * Mapping of legacy DefaultGroup values to new UI grouping values.
 *
 * Legacy uses database field identifiers (e.g., "InstitutionId", "AccountId"),
 * while the new UI uses semantic identifiers (e.g., "institution", "account").
 */
const ACCOUNTS_GROUP_MAP: Record<string, string> = {
  none: 'none',
  InstitutionId: 'institution',
  // Legacy calls this "Balance Sheet Category" (ClassificationId field).
  // Renamed to "data source" per product decision for new UI.
  ClassificationId: 'data source',
};

const HOLDINGS_GROUP_MAP: Record<string, string> = {
  none: 'none',
  AccountId: 'account',
  DiversificationId: 'none', // No direct mapping in new UI, fall back to none
  SubDiversificationId: 'none', // No direct mapping in new UI, fall back to none
  Name: 'none', // Security grouping not available in new UI
};

const CASHFLOW_GROUP_MAP: Record<string, string> = {
  none: 'none',
  ParentCategoryId: 'none', // Top-Level Category - not yet implemented in new UI
  BankTransactionCategoryId: 'none', // Category - not yet implemented in new UI
};

const TAXLOTS_GROUP_MAP: Record<string, string> = {
  none: 'no-group',
  '': 'no-group', // Empty string means "None" in legacy
  WealthAccountId: 'by-account',
  TaxLotTerm: 'by-term',
  GainType: 'by-gain-loss',
  DiversificationId: 'by-asset-allocation',
};

/**
 * Mapping of legacy DefaultView values to new UI ViewMode values.
 *
 * Legacy view IDs come from the views array in each page component.
 * New UI uses ViewMode enum values (table, chart, historical, etc.)
 */
const ACCOUNTS_VIEW_MAP: Record<string, string> = {
  balances: 'table',
  charts: 'chart',
  trend: 'historical',
};

const HOLDINGS_VIEW_MAP: Record<string, string> = {
  balances: 'table',
  trend: 'historical',
};

const ASSET_ALLOCATION_VIEW_MAP: Record<string, string> = {
  chart: 'pie',
  trend: 'historical',
  balances: 'table',
};

const CASHFLOW_VIEW_MAP: Record<string, string> = {
  transactions: 'table',
  historicalTrend: 'bar',
  // Legacy fallbacks
  list: 'table',
  charts: 'bar',
};

const TAXLOTS_VIEW_MAP: Record<string, string> = {
  list: 'table',
  charts: 'chart',
};

const BALANCE_SHEET_VIEW_MAP: Record<string, string> = {
  balances: 'table',
  chart: 'chart',
  trend: 'historical',
  // "summary" (pie chart) is a new UI option not in legacy, but we support passthrough
};

/**
 * Translates a legacy DefaultGroup value to the new UI grouping value.
 *
 * @param legacyValue - The DefaultGroup value from the Menu configuration
 * @param pageContext - The type of page to determine which translation map to use
 * @returns The translated new UI grouping value, or "none" if not found
 *
 * @example
 * translateDefaultGroup("InstitutionId", "accounts") // Returns "institution"
 * translateDefaultGroup("AccountId", "holdings") // Returns "account"
 * translateDefaultGroup("investor:123", "accounts") // Returns "investor:123" (passthrough)
 */
export function translateDefaultGroup(
  legacyValue: string | null | undefined,
  pageContext: PageContext,
): string {
  // Handle null/undefined
  if (!legacyValue) {
    return 'none';
  }

  // Custom tag groupings are passed through as-is
  // Format: "investor:{tagCategoryId}" or "advisor:{tagCategoryId}"
  if (
    legacyValue.startsWith('investor:') ||
    legacyValue.startsWith('advisor:')
  ) {
    return legacyValue;
  }

  // Select the appropriate translation map based on page context
  const translationMaps: Record<PageContext, Record<string, string>> = {
    accounts: ACCOUNTS_GROUP_MAP,
    holdings: HOLDINGS_GROUP_MAP,
    cashflow: CASHFLOW_GROUP_MAP,
    taxlots: TAXLOTS_GROUP_MAP,
    assetAllocation: HOLDINGS_GROUP_MAP, // Asset Allocation uses same mappings as holdings
    diversifications: HOLDINGS_GROUP_MAP, // Diversifications uses same mappings as holdings
    balanceSheet: ACCOUNTS_GROUP_MAP, // Balance Sheet uses same group mappings as accounts
  };

  const translationMap = translationMaps[pageContext];

  // Look up the translation, falling back to "none" if not found
  const translated = translationMap[legacyValue];
  if (translated !== undefined) {
    return translated;
  }

  // If not found in specific map, check if it's already a valid new UI value
  const validNewUIValues = [
    'none',
    'institution',
    'data source',
    'account', // accounts/holdings
    'no-group',
    'by-holding',
    'by-account',
    'by-term',
    'by-gain-loss',
    'by-asset-allocation', // taxlots (GainLossGroupByMode)
  ];
  if (validNewUIValues.includes(legacyValue.toLowerCase())) {
    return legacyValue.toLowerCase();
  }

  // Log warning for unmapped values in development
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      `[pageDefaultsTranslation] Unknown DefaultGroup value: "${legacyValue}" for context "${pageContext}". Falling back to "none".`,
    );
  }

  return 'none';
}

/**
 * Translates a legacy DefaultView value to the new UI view value.
 *
 * @param legacyValue - The DefaultView value from the Menu configuration
 * @param pageContext - The type of page to determine which translation map to use
 * @returns The translated new UI view value, or undefined if not set
 *
 * @example
 * translateDefaultView("balances", "accounts") // Returns "table"
 * translateDefaultView("charts", "accounts") // Returns "chart"
 * translateDefaultView("trend", "accounts") // Returns "historical"
 */
export function translateDefaultView(
  legacyValue: string | null | undefined,
  pageContext?: PageContext,
): string | undefined {
  if (!legacyValue) {
    return undefined;
  }

  // Select the appropriate translation map based on page context
  const translationMaps: Record<PageContext, Record<string, string>> = {
    accounts: ACCOUNTS_VIEW_MAP,
    holdings: HOLDINGS_VIEW_MAP,
    assetAllocation: ASSET_ALLOCATION_VIEW_MAP,
    cashflow: CASHFLOW_VIEW_MAP,
    taxlots: TAXLOTS_VIEW_MAP,
    diversifications: HOLDINGS_VIEW_MAP, // Diversifications uses same mappings as holdings
    balanceSheet: BALANCE_SHEET_VIEW_MAP,
  };

  if (pageContext) {
    const translationMap = translationMaps[pageContext];
    const translated = translationMap[legacyValue];
    if (translated !== undefined) {
      return translated;
    }
  }

  // If not found in specific map, check if it's already a valid new UI value
  const validNewUIValues = [
    'table',
    'chart',
    'historical',
    'summary',
    'pie',
    'bar',
  ];
  if (validNewUIValues.includes(legacyValue.toLowerCase())) {
    return legacyValue.toLowerCase();
  }

  // Return undefined for unknown values (will use default)
  return undefined;
}

/**
 * Determines the page context from a route path.
 *
 * @param path - The current route path (e.g., "/Portfolio/Accounts")
 * @returns The PageContext for translation
 */
/**
 * Determines the page context from a route path.
 *
 * @param path - The current route path (e.g., "/Portfolio/Accounts")
 * @returns The PageContext for translation, or undefined if path doesn't match a known page
 */
export function getPageContextFromPath(path: string): PageContext | undefined {
  const normalizedPath = path.toLowerCase();

  if (normalizedPath.includes('/accounts')) {
    return 'accounts';
  }
  if (normalizedPath.includes('/holdings')) {
    return 'holdings';
  }
  if (
    normalizedPath.includes('/cashflow') ||
    normalizedPath.includes('/cash-flow')
  ) {
    return 'cashflow';
  }
  if (
    normalizedPath.includes('/taxlots') ||
    normalizedPath.includes('/gains')
  ) {
    return 'taxlots';
  }
  if (normalizedPath.includes('/diversification')) {
    return 'diversifications';
  }
  if (
    normalizedPath.includes('/balancesheet') ||
    normalizedPath.includes('/balance-sheet')
  ) {
    return 'balanceSheet';
  }

  return undefined;
}
